import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

function getAuthHeaders(): HeadersInit {
  const accessToken = useAuthStore.getState().accessToken
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
}

interface BillingStatus {
  hasSubscription: boolean
  paidSeats: number
  allowed: number
  used: number
  remaining: number
  subscriptionId: string | null
}

interface CheckoutResponse {
  checkoutUrl: string
}

interface PortalResponse {
  portalUrl: string
}

interface SyncResponse {
  synced: boolean
  hasSubscription: boolean
  seats: number
}

interface UpdateSeatsResponse {
  success: boolean
  seats: number
}


/**
 * Get current billing/subscription status
 */
export async function getBillingStatus(): Promise<BillingStatus> {
  const response = await fetch(`${API_URL}/api/billing/status`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get billing status')
  }

  return response.json()
}

/**
 * Create a checkout session for purchasing seats
 * @param seats - Number of child seats to purchase (minimum 2)
 */
export async function createCheckout(seats: number): Promise<CheckoutResponse> {
  const response = await fetch(`${API_URL}/api/billing/checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ seats }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create checkout')
  }

  return response.json()
}

/**
 * Get Stripe customer portal URL for managing subscription
 */
export async function getPortalUrl(): Promise<PortalResponse> {
  const response = await fetch(`${API_URL}/api/billing/portal`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get portal URL')
  }

  return response.json()
}

/**
 * Manually sync subscription status from Salable
 * Useful if webhook failed or for debugging
 */
export async function syncSubscription(): Promise<SyncResponse> {
  const response = await fetch(`${API_URL}/api/billing/sync`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to sync subscription')
  }

  return response.json()
}

/**
 * Update seat count on existing subscription
 * @param seats - New number of seats (minimum 2)
 */
export async function updateSeats(seats: number): Promise<UpdateSeatsResponse> {
  const response = await fetch(`${API_URL}/api/billing/seats`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ seats }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to update seats')
  }

  return response.json()
}
